import React, { useRef, useState, useEffect } from 'react';
import {
    Button,
    makeStyles,
    Caption1,
    Text,
    tokens,
} from '@fluentui/react-components';
import { Add16Regular, Checkmark16Regular } from '@fluentui/react-icons';
import { ContextItem, fetchProductLogo } from '../../Service/ServerAPI';
import { SearchResults } from '../../types/ArtifactTypes';
import { useDispatch, useSelector } from 'react-redux';
import { addToContext, RootState } from '../../utils/store';
import defaultImage from '../../images/no-image.jpg';
import ProductNamesBadge from './ProductNamesBadge';

const useStyles = makeStyles({
    header: {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: '#25282C',
        padding: '18px',
    },
    content: {
        display: 'flex',
        flexDirection: 'row',
        gap: '24px',
        width: '100%',
    },
    howToUse: {
        display: 'flex',
        flexDirection: 'column',
        width: '19.5vw',
        gap: '24px',
        backgroundColor: '#282B30',
        padding: '24px',
    },
    searchResultsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        backgroundColor: '#282B30',
        padding: '24px',
        overflowY: 'auto',
        height: '50vh',
    },
    descriptionDisplay: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        gap: '8px',
        width: '30vw',
        backgroundColor: '#282B30',
    },
    card: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1A1B1E',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        height: 'fit-content',
        minHeight: '200px',
        ':hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
        },
    },
    cardAdded: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1A1B1E',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        border: `2px solid ${tokens.colorBrandBackground2}`,
        opacity: '0.9',
        height: 'fit-content',
        minHeight: '200px',
        ':hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 8px 25px rgba(0, 78, 212, 0.3)',
        },
    },
    imageContainer: {
        position: 'relative',
        width: '100%',
        aspectRatio: '1/1',
        overflow: 'hidden',
        backgroundColor: '#2D3135',
        flexShrink: 0,
    },
    headerImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        backgroundColor: '#2D3135',
    },
    overlayButton: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        transition: 'all 0.2s ease',
        color: 'white',
        ':hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            transform: 'scale(1.1)',
        },
    },
    overlayButtonAdded: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tokens.colorBrandBackground2,
        border: '1px solid rgba(255, 255, 255, 0.2)',
        transition: 'all 0.2s ease',
        color: 'white',
    },
    contentSection: {
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    title: {
        fontSize: '14px',
        fontWeight: '600',
        color: tokens.colorNeutralForeground1,
        lineHeight: '1.3',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        marginBottom: '4px',
    },
    metadata: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground3,
        lineHeight: '1.2',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    caption: {
        color: tokens.colorNeutralForeground3,
        lineHeight: '1.2',
    },
    text: {
        margin: '0',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: '1.2',
    },
    searchSelectContainer: {
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
    },
    headerTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: '250px',
        flex: '1',
        overflow: 'hidden',
    },
    searchBoxContainer: {
        flexGrow: 1,
        flex: '1',
        minWidth: '300px',
    },
    selectContainer: {
        minWidth: '150px',
    },
    actionButton: {
        marginLeft: 'auto',
    },
});

//import { useRef, useEffect, useState } from 'react';

const useFitText = (maxFontSize = 16, minFontSize = 8) => {
    const ref = useRef<HTMLDivElement>(null); // Explicitly type as HTMLDivElement
    const [fontSize, setFontSize] = useState(maxFontSize);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        let resizeTimeout: NodeJS.Timeout;

        const adjustFontSize = () => {
            let currentFontSize = maxFontSize;
            element.style.fontSize = `${currentFontSize}px`;

            while (
                element.scrollWidth > element.clientWidth &&
                currentFontSize > minFontSize
            ) {
                currentFontSize -= 1;
                element.style.fontSize = `${currentFontSize}px`;
            }
            setFontSize(currentFontSize);
        };

        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                adjustFontSize();
            }, 100); // Adjust the debounce delay as needed
        };

        const resizeObserver = new ResizeObserver(handleResize);

        resizeObserver.observe(element);

        return () => {
            clearTimeout(resizeTimeout);
            resizeObserver.disconnect();
        };
    }, [maxFontSize, minFontSize]);

    return { ref, fontSize };
};

// Helper function to check if a search result is already in context
const isItemInContext = (searchResult: SearchResults, context: { [key: string]: ContextItem }): boolean => {
    // Check all context items to see if this search result matches any of them
    return Object.values(context).some(contextItem => {
        // Match by different field combinations based on the type
        if ((searchResult.Type === 'Game' || searchResult.Type === 'App') && searchResult.Id) {
            return contextItem.FieldName === 'TitleId' && contextItem.Value === searchResult.Id;
        } else if (searchResult.Type === 'Product' && searchResult.Id) {
            return contextItem.FieldName === 'ProductId' && contextItem.Value === searchResult.Id;
        } else if (searchResult.Type === 'Subscription' && searchResult.Id) {
            return contextItem.FieldName === 'ProductId' && contextItem.Value === searchResult.Id;
        } else {
            // For other types, match by name and type
            return contextItem.FieldName === searchResult.Type && contextItem.Value === searchResult.Name;
        }
    });
};

// This could be a new component for rendering individual search result items
const SearchResultItem: React.FC<{ result: SearchResults }> = ({ result }) => {
    const [imageUrl, setImageUrl] = useState<string>('default-image-url');
    const classes = useStyles();
    const dispatch = useDispatch();
    const { ref: nameRef } = useFitText(16, 10); // Adjust max and min font sizes as needed
    
    // Get current context from Redux store
    const context = useSelector((state: RootState) => state.settings.context);
    
    // Check if this item is already in context
    const isAdded = isItemInContext(result, context);

    let platforms: string[] = [];
    try {
        const platformsData = result.AdditionalData?.Platforms;
        if (platformsData) {
            platforms = Array.isArray(platformsData)
                ? platformsData
                : platformsData.split(',');
        }
    } catch (error) {
        console.error('Failed to parse Platforms:', error);
    }
    const handleAddToContext = (value: SearchResults) => {
        // Don't add if already in context
        if (isAdded) {
            return;
        }
        
        //SearchResult to ContextItem
        if ((value.Type === 'Game' || value.Type === 'App') && value.Id) {
            var item: ContextItem = {
                DisplayName: value.Name,
                FieldName: 'TitleId',
                Value: value.Id,
                ContextType: value.AdditionalData?.ProductType ?? 'Game',
            };

            dispatch(addToContext({ key: item.Value, value: item }));
            //item.DisplayName: value.Name;
        } else if (value.Type === 'Product' && value.Id) {
            var item: ContextItem = {
                DisplayName: value.Name + ' (' + value.Id + ')',
                FieldName: 'ProductId',
                Value: value.Id,
                ContextType: value.ProductType ?? 'Game',
            };

            dispatch(addToContext({ key: item.DisplayName, value: item }));
            //item.DisplayName: value.Name;
        } else if (value.Type === 'Subscription' && value.Id) {
            var item: ContextItem = {
                DisplayName: value.Name,
                FieldName: 'ProductId',
                Value: value.Id,
                ContextType: 'Subscription',
            };

            dispatch(addToContext({ key: item.DisplayName, value: item }));
            //item.DisplayName: value.Name;
        } else {
            var item: ContextItem = {
                DisplayName: value.Name + ' (' + value.Type + ')',
                FieldName: value.Type,
                Value: value.Name,
                ContextType: 'Game',
            };

            dispatch(addToContext({ key: item.DisplayName, value: item }));
        }
    };
    useEffect(() => {
        let mounted = true;
        const updateImageUrl = async () => {
            try {
                if (result.AdditionalData?.ProductId) {
                    const url = await fetchProductLogo(
                        result.AdditionalData.ProductId
                    );
                    if (mounted) {
                        setImageUrl(url);
                    }
                } else if (
                    (result.Type === 'Product' ||
                        result.Type === 'Subscription') &&
                    result.Id
                ) {
                    const url = await fetchProductLogo(result.Id);
                    if (mounted) {
                        setImageUrl(url);
                    }
                }
            } catch (error) {
                console.error('Error fetching image URL:', error);
            }
        };
        updateImageUrl();
        return () => {
            mounted = false;
        };
    }, [result.AdditionalData?.ProductId]);
    return (
        <div className={isAdded ? classes.cardAdded : classes.card}>
            <div className={classes.imageContainer}>
                <img
                    className={classes.headerImage}
                    src={imageUrl}
                    alt={result.Name}
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = defaultImage;
                    }}
                />
                <button
                    className={isAdded ? classes.overlayButtonAdded : classes.overlayButton}
                    onClick={() => handleAddToContext(result)}
                    disabled={isAdded}
                    aria-label={isAdded ? `${result.Name} added` : `Add ${result.Name}`}
                >
                    {isAdded ? <Checkmark16Regular /> : <Add16Regular />}
                </button>
            </div>
            
            <div className={classes.contentSection}>
                <div className={classes.title} title={result.Name}>
                    {result.Name}
                </div>
                <div className={classes.metadata}>
                    {result.ProductType ?? result.Type}
                    {((result.Type === 'Game' || result.Type === 'App') && result.Id) && (
                        <span> ({result.Id})</span>
                    )}
                    {(result.Type === 'Product' && result.Id) && (
                        <span> ({result.Id})</span>
                    )}
                </div>
                {platforms && platforms.length > 0 && (
                    <div className={classes.metadata} title={platforms.join(', ')}>
                        {platforms.slice(0, 2).join(', ')}{platforms.length > 2 ? '...' : ''}
                    </div>
                )}
                <ProductNamesBadge productNames={result.AdditionalData?.ProductNames} />
            </div>
        </div>
    );
};

export default SearchResultItem;
